import { Icon } from '@anyroad/icon';
import styled from '@emotion/styled';
import { colors } from 'ui_system/compass';

export const CapacityError = styled.div<{ marginTop?: string }>`
  display: flex;
  align-items: center;
  width: 100%;
  color: ${colors.white};
  background-color: ${colors.warningOrange};
  font-size: 14px;
  line-height: 18px;
  font-weight: 400;
  padding: 5px 10px;
  margin-top: ${(props) => props.marginTop};
`;

export const CapacityWarning = ({
  overBookingMessage,
  marginTop,
}: {
  overBookingMessage: string;
  marginTop?: string;
}) => {
  if (!overBookingMessage) return null;

  return (
    <CapacityError marginTop={marginTop} data-testid="ticket-capacity-warning">
      <Icon height={16} name="Warning" stroke="white" fill="white" />
      <p>{overBookingMessage}</p>
    </CapacityError>
  );
};

export const formatOverbookingMessage = (
  overbookedTicketCapacities: { unitName: string; overbookedBy: number }[],
  hasBookingUpdated: boolean = false
) => {
  if (!overbookedTicketCapacities || overbookedTicketCapacities.length === 0) {
    return '';
  }

  if (overbookedTicketCapacities.length === 1) {
    const item = overbookedTicketCapacities[0];

    if (hasBookingUpdated) {
      return `You're about to overbook ${item.unitName} tickets by ${item.overbookedBy}. Continue if OK.`;
    }

    return `Overbooked ${item.unitName} tickets by ${item.overbookedBy}.`;
  }

  const formattedItems = overbookedTicketCapacities.map((item, index) => {
    if (index === overbookedTicketCapacities.length - 2) {
      return `${item.unitName} tickets by ${item.overbookedBy} and`;
    }
    if (index === overbookedTicketCapacities.length - 1) {
      return `${item.unitName} tickets by ${item.overbookedBy}`;
    }
    return `${item.unitName} tickets by ${item.overbookedBy},`;
  });

  if (hasBookingUpdated) {
    return `You're about to overbook ${formattedItems.join(' ')}. Continue if OK.`;
  }

  return `Overbooked ${formattedItems.join(' ')}.`;
};

export const getSingleTicketOverbookingValue = (
  unitTypeId: number,
  guestList: { unitType: number; id: number }[],
  unitCapacity: { availability: number; unit_name: string }
) => {
  if (!unitTypeId || !unitCapacity) return null;

  // Count only newly added guests for this unitType
  // We will exclude the guests that are already in the guestList
  const newGuestCount = guestList.filter(
    (guest) => guest.unitType === unitTypeId && guest.id === null
  ).length;

  const { availability = 0, unit_name = '' } = unitCapacity;

  const newAvailability = availability - newGuestCount;

  // We need to add 1 to the overbooking because the new guest is not yet added to the capacity
  const overbookedBy = newAvailability <= 0 ? Math.abs(newAvailability) + 1 : 0;

  return overbookedBy > 0
    ? `Overbooking ${unit_name} by ${overbookedBy}.`
    : null;
};
